import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Power, PowerOff, Calendar, Eye, MousePointer, XCircle, TrendingUp, Link2, QrCode, Users, ExternalLink, MoreVertical, Settings } from 'lucide-react';
import MediaUploader from '../components/MediaUploader';
import { useToast } from '../hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnnouncementsManager = () => {
  const { toast } = useToast();
  const [announcements, setAnnouncements] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [automationSettings, setAutomationSettings] = useState({
    auto_generation_enabled: true,
    generation_frequency_hours: 1,
    default_cta_link: '/contact'
  });
  const [formData, setFormData] = useState({
    type: 'banner',
    title: '',
    message: '',
    icon: '📢',
    cta_text: '',
    cta_link: '',
    media_type: 'none',
    media_url: '',
    media_thumbnail: '',
    media_orientation: 'landscape',
    modal_layout: 'hero',
    secondary_cta_text: '',
    secondary_cta_link: '',
    theme: 'professional',
    background_color: '#8b5cf6',
    text_color: '#ffffff',
    start_date: '',
    end_date: '',
    is_active: true,
    dismiss_behavior: 'session',
    auto_rotate: true,
    rotation_interval: 5,
    priority: 0,
    enable_lead_capture: false,
    public_slug: ''
  });

  useEffect(() => {
    fetchAnnouncements();
    fetchAutomationSettings();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
      toast({ title: "Error", description: "Failed to load announcements", variant: "destructive" });
    }
  };

  const fetchAutomationSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/announcement-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutomationSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const updateAutomationSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/announcement-settings`, automationSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Automation settings updated" });
      setShowSettings(false);
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast({ title: "Error", description: "Failed to update settings", variant: "destructive" });
    }
  };

  // Format date for datetime-local input (preserves local timezone)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Get local date components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Generate slug and CTA link from title
  const generateSlugFromTitle = (title) => {
    if (!title || title.trim() === '') return '';
    
    // Remove emojis and special characters
    let slug = title.replace(/[^\w\s-]/g, '');
    // Convert to lowercase and replace spaces with hyphens
    slug = slug.toLowerCase().replace(/[-\s]+/g, '-').trim('-');
    // Add unique suffix (timestamp)
    const uniqueSuffix = Date.now().toString().slice(-6);
    slug = `${slug}-${uniqueSuffix}`;
    
    return slug;
  };

  // Handle title change and auto-generate CTA link
  const handleTitleChange = (newTitle) => {
    setFormData(prev => ({ ...prev, title: newTitle }));
    
    // Auto-generate slug and CTA link if not editing or if CTA link is empty/default
    if (!editingAnnouncement || !formData.cta_link || formData.cta_link === '/contact' || formData.cta_link === '') {
      const slug = generateSlugFromTitle(newTitle);
      if (slug) {
        const promoLink = `/promo/${slug}`;
        setFormData(prev => ({ 
          ...prev, 
          title: newTitle,
          public_slug: slug,
          cta_link: promoLink,
          secondary_cta_link: promoLink, // Also set secondary CTA link
          enable_lead_capture: true
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      
      // Format dates for API
      const payload = { ...formData };
      if (payload.start_date) {
        payload.start_date = new Date(payload.start_date).toISOString();
      } else {
        payload.start_date = null;
      }
      if (payload.end_date) {
        payload.end_date = new Date(payload.end_date).toISOString();
      } else {
        payload.end_date = null;
      }

      if (editingAnnouncement) {
        await axios.put(`${API}/announcements/${editingAnnouncement.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: "Success", description: "Announcement updated successfully" });
      } else {
        await axios.post(`${API}/announcements`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: "Success", description: "Announcement created successfully" });
      }

      setShowModal(false);
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to save announcement:', error);
      toast({ title: "Error", description: "Failed to save announcement", variant: "destructive" });
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      ...announcement,
      // Ensure these fields have default values if missing
      media_type: announcement.media_type || 'none',
      media_url: announcement.media_url || '',
      media_thumbnail: announcement.media_thumbnail || '',
      media_orientation: announcement.media_orientation || 'landscape',
      modal_layout: announcement.modal_layout || 'hero',
      secondary_cta_text: announcement.secondary_cta_text || '',
      secondary_cta_link: announcement.secondary_cta_link || '',
      enable_lead_capture: announcement.enable_lead_capture || false,
      public_slug: announcement.public_slug || '',
      start_date: announcement.start_date ? formatDateForInput(announcement.start_date) : '',
      end_date: announcement.end_date ? formatDateForInput(announcement.end_date) : ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    // Show confirmation dialog FIRST, before any async operations
    const confirmed = window.confirm('Are you sure you want to delete this announcement? This action cannot be undone.');
    
    if (!confirmed) {
      return; // User cancelled - exit immediately
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Announcement deleted successfully" });
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      toast({ title: "Error", description: "Failed to delete announcement", variant: "destructive" });
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/announcements/${id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Status updated successfully" });
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to toggle status:', error);
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const handleDeleteAllAutoGenerated = async () => {
    const autoCount = announcements.filter(a => a.auto_generated).length;
    
    if (autoCount === 0) {
      toast({ title: "Info", description: "No auto-generated announcements to delete" });
      return;
    }

    if (!window.confirm(`Delete all ${autoCount} auto-generated announcements? This cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.delete(`${API}/announcements/automation/delete-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: response.data.message });
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to delete auto-generated announcements:', error);
      toast({ title: "Error", description: "Failed to delete announcements", variant: "destructive" });
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'banner',
      title: '',
      message: '',
      icon: '📢',
      cta_text: '',
      cta_link: '',
      media_type: 'none',
      media_url: '',
      media_thumbnail: '',
      media_orientation: 'landscape',
      modal_layout: 'hero',
      secondary_cta_text: '',
      secondary_cta_link: '',
      theme: 'professional',
      background_color: '#8b5cf6',
      text_color: '#ffffff',
      start_date: '',
      end_date: '',
      is_active: true,
      dismiss_behavior: 'session',
      auto_rotate: true,
      rotation_interval: 5,
      priority: 0,
      enable_lead_capture: false,
      public_slug: ''
    });
    setEditingAnnouncement(null);
  };


  // Lead Capture Actions
  const handleCopyUrl = async (announcement) => {
    if (!announcement.enable_lead_capture || !announcement.public_slug) {
      toast({ title: "Error", description: "Lead capture not enabled for this announcement", variant: "destructive" });
      return;
    }

    const publicUrl = `${window.location.origin}/promo/${announcement.public_slug}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast({ title: "Success", description: "URL copied to clipboard!" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to copy URL", variant: "destructive" });
    }
  };

  const handleDownloadQR = async (announcementId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/announcements/${announcementId}/qr-code`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `announcement-qr-${announcementId}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({ title: "Success", description: "QR code downloaded successfully" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to download QR code", variant: "destructive" });
    }
  };

  const handleViewLeads = (announcement) => {
    if (!announcement.enable_lead_capture) {
      toast({ title: "Info", description: "Lead capture not enabled for this announcement" });
      return;
    }
    
    const leadCount = announcement.metrics?.form_submissions || 0;
    toast({ 
      title: "Lead Stats", 
      description: `${leadCount} lead(s) captured from this announcement. Check Lead Management for details.` 
    });
  };

  const handlePreview = (announcement) => {
    if (!announcement.enable_lead_capture || !announcement.public_slug) {
      toast({ title: "Error", description: "Lead capture not enabled", variant: "destructive" });
      return;
    }

    const previewUrl = `/promo/${announcement.public_slug}`;
    window.open(previewUrl, '_blank');
  };


  const iconOptions = ['📢', '🎉', '🎊', '⚠️', '✨', '🔥', '💫', '🎁', '🌟', '📣'];

  return (
    <div className="admin-content-wrapper">
      <div className="admin-header">
        <div>
          <h1 className="heading-1">Announcements Manager</h1>
          <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
            Manage banners and modal announcements
          </p>
          {automationSettings.auto_generation_enabled && (
            <div style={{ 
              marginTop: '0.5rem', 
              padding: '0.5rem 0.75rem', 
              backgroundColor: '#f0fdf4', 
              color: '#16a34a', 
              borderRadius: '6px', 
              fontSize: '0.875rem',
              display: 'inline-block'
            }}>
              🤖 Auto-generation enabled • Running {
                automationSettings.generation_frequency_hours === 1 ? 'every hour' :
                automationSettings.generation_frequency_hours === 24 ? 'daily' :
                automationSettings.generation_frequency_hours === 168 ? 'weekly' :
                automationSettings.generation_frequency_hours < 24 ? `every ${automationSettings.generation_frequency_hours} hours` :
                `every ${Math.floor(automationSettings.generation_frequency_hours / 24)} days`
              }
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => setShowSettings(true)}
            className="btn-secondary"
            title="Automation Settings"
          >
            <Settings size={18} />
            <span>Automation Settings</span>
          </button>
          {announcements.filter(a => a.auto_generated).length > 0 && (
            <button
              onClick={handleDeleteAllAutoGenerated}
              className="btn-danger"
              title="Delete all auto-generated announcements"
            >
              <Trash2 size={18} />
              <span>Delete Auto ({announcements.filter(a => a.auto_generated).length})</span>
            </button>
          )}
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn-primary"
          >
            <Plus size={18} />
            <span>Add Announcement</span>
          </button>
        </div>
      </div>

      {announcements.length === 0 ? (
        <div className="empty-state">
          <p>No announcements yet. Create your first announcement!</p>
        </div>
      ) : (
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Title</th>
                <th>Theme</th>
                <th>Performance</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {announcements.map((announcement) => {
                const metrics = announcement.metrics || { impressions: 0, clicks: 0, dismissals: 0 };
                const ctr = metrics.impressions > 0 
                  ? ((metrics.clicks / metrics.impressions) * 100).toFixed(1) 
                  : 0;
                const engagementRate = metrics.impressions > 0 
                  ? (((metrics.clicks + metrics.dismissals) / metrics.impressions) * 100).toFixed(1) 
                  : 0;

                return (
                  <tr key={announcement.id}>
                    <td>
                      <span className={`badge ${announcement.type === 'banner' ? 'badge-primary' : 'badge-secondary'}`}>
                        {announcement.type}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>{announcement.icon}</span>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <strong>{announcement.title}</strong>
                            {announcement.auto_generated && (
                              <span style={{ 
                                fontSize: '0.65rem', 
                                padding: '2px 6px', 
                                background: '#8b5cf6', 
                                color: 'white', 
                                borderRadius: '4px',
                                fontWeight: 'bold'
                              }}>
                                🤖 AUTO
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                            Priority: {announcement.priority}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="badge">{announcement.theme}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: '200px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                          <Eye size={14} style={{ color: 'var(--accent-purple-400)' }} />
                          <span><strong>{metrics.impressions}</strong> impressions</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                          <MousePointer size={14} style={{ color: '#10b981' }} />
                          <span><strong>{metrics.clicks}</strong> clicks <span style={{ color: 'var(--text-muted)' }}>({ctr}% CTR)</span></span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                          <XCircle size={14} style={{ color: '#ef4444' }} />
                          <span><strong>{metrics.dismissals}</strong> dismissals</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                          <TrendingUp size={14} style={{ color: '#f59e0b' }} />
                          <span><strong>{engagementRate}%</strong> engagement</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <span className={`badge ${announcement.is_active ? 'badge-success' : 'badge-muted'}`}>
                          {announcement.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {announcement.end_date && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            Ends: {new Date(announcement.end_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <button
                          onClick={() => handleToggleStatus(announcement.id)}
                          className="btn-icon"
                          title={announcement.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {announcement.is_active ? <PowerOff size={16} /> : <Power size={16} />}
                        </button>
                        <button
                          onClick={() => handleEdit(announcement)}
                          className="btn-icon"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDelete(announcement.id);
                          }}
                          className="btn-icon btn-danger"
                          title="Delete"
                          type="button"
                        >
                          <Trash2 size={16} />
                        </button>
                        
                        {/* Lead Capture Actions - Dropdown Menu */}
                        {announcement.enable_lead_capture && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="btn-icon"
                                title="Promo Actions"
                                type="button"
                                style={{ color: '#8b5cf6' }}
                              >
                                <MoreVertical size={16} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" style={{ minWidth: '200px' }}>
                              <DropdownMenuItem onClick={() => handleCopyUrl(announcement)}>
                                <Link2 size={16} style={{ marginRight: '8px', color: '#10b981' }} />
                                Copy Public URL
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownloadQR(announcement.id)}>
                                <QrCode size={16} style={{ marginRight: '8px', color: '#8b5cf6' }} />
                                Download QR Code
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleViewLeads(announcement)}>
                                <Users size={16} style={{ marginRight: '8px', color: '#f59e0b' }} />
                                View Leads ({announcement.metrics?.form_submissions || 0})
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handlePreview(announcement)}>
                                <ExternalLink size={16} style={{ marginRight: '8px', color: '#3b82f6' }} />
                                Preview Landing Page
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => {
          setShowModal(false);
          setEditingAnnouncement(null);
          resetForm();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <h2 className="heading-2">{editingAnnouncement ? 'Edit' : 'Add'} Announcement</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Type *</label>
                  <select
                    className="form-input"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    required
                  >
                    <option value="banner">Top Banner</option>
                    <option value="modal">Modal Popup</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Icon</label>
                  <select
                    className="form-input"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  >
                    {iconOptions.map(icon => (
                      <option key={icon} value={icon}>{icon} {icon}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Title *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="e.g., Happy Diwali! Special Offer"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Message *</label>
                <textarea
                  className="form-input"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Your announcement message"
                  rows={3}
                  required
                />
              </div>

              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">CTA Button Text</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                    placeholder="Book Now"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">CTA Link</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.cta_link}
                    onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                    placeholder="Auto-generated from title"
                    style={{ backgroundColor: formData.cta_link && formData.cta_link.startsWith('/promo/') ? '#f0fdf4' : 'white' }}
                  />
                  {formData.cta_link && formData.cta_link.startsWith('/promo/') && (
                    <small style={{ color: '#16a34a', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                      ✅ Auto-generated unique landing page URL. Lead capture enabled.
                    </small>
                  )}
                  {!formData.cta_link && (
                    <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                      CTA link will be auto-generated from title
                    </small>
                  )}
                </div>
              </div>

              {/* Rich Media Section */}
              {formData.type === 'modal' && (
                <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '8px', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#374151' }}>
                    🎬 Rich Media (Images/Videos)
                  </h3>
                  
                  <div className="form-group">
                    <label className="form-label">Media Type</label>
                    <select
                      className="form-input"
                      value={formData.media_type}
                      onChange={(e) => setFormData({ ...formData, media_type: e.target.value })}
                    >
                      <option value="none">No Media</option>
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </select>
                  </div>

                  {formData.media_type !== 'none' && (
                    <>
                      <div className="form-group">
                        <label className="form-label">Media Orientation</label>
                        <select
                          className="form-input"
                          value={formData.media_orientation}
                          onChange={(e) => setFormData({ ...formData, media_orientation: e.target.value })}
                        >
                          <option value="landscape">Landscape (16:9 - Wide)</option>
                          <option value="square">Square (1:1)</option>
                          <option value="portrait">Portrait (9:16 - Tall)</option>
                        </select>
                        <small style={{ color: '#6b7280', display: 'block', marginTop: '0.5rem' }}>
                          Choose how the media should be displayed in the modal
                        </small>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Upload {formData.media_type === 'image' ? 'Image' : 'Video'}</label>
                        <MediaUploader
                          type={formData.media_type}
                          currentUrl={formData.media_url}
                          onUploadComplete={(data) => {
                            setFormData({ 
                              ...formData, 
                              media_url: data.url || '' 
                            });
                          }}
                        />
                        <small style={{ color: '#6b7280', display: 'block', marginTop: '0.5rem' }}>
                          {formData.media_type === 'image' 
                            ? `Recommended: ${
                                formData.media_orientation === 'landscape' ? '1200 × 675 px (16:9)' :
                                formData.media_orientation === 'square' ? '1080 × 1080 px' :
                                '1080 × 1920 px'
                              }, JPG/PNG, max 10MB` 
                            : `Recommended: ${
                                formData.media_orientation === 'landscape' ? '1200 × 675 px (16:9)' :
                                formData.media_orientation === 'square' ? '1080 × 1080 px' :
                                '1080 × 1920 px'
                              }, MP4, max 10MB`}
                        </small>
                      </div>

                      {formData.media_type === 'video' && (
                        <div className="form-group">
                          <label className="form-label">Video Thumbnail (Optional)</label>
                          <MediaUploader
                            type="image"
                            currentUrl={formData.media_thumbnail}
                            onUploadComplete={(data) => {
                              setFormData({ 
                                ...formData, 
                                media_thumbnail: data.url || '' 
                              });
                            }}
                          />
                          <small style={{ color: '#6b7280' }}>Upload a thumbnail image for video preview</small>
                        </div>
                      )}

                      <div className="form-group">
                        <label className="form-label">Modal Layout</label>
                        <select
                          className="form-input"
                          value={formData.modal_layout}
                          onChange={(e) => setFormData({ ...formData, modal_layout: e.target.value })}
                        >
                          <option value="hero">Hero (Full width overlay)</option>
                          <option value="split">Split (50-50)</option>
                          <option value="image_left">Image Left + Text Right</option>
                          <option value="image_right">Text Left + Image Right</option>
                          <option value="card">Card (Compact)</option>
                          {formData.media_type === 'video' && <option value="video_bg">Video Background</option>}
                        </select>
                      </div>
                    </>
                  )}

                  <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label className="form-label">Secondary CTA Text</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.secondary_cta_text}
                        onChange={(e) => setFormData({ ...formData, secondary_cta_text: e.target.value })}
                        placeholder="Learn More"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Secondary CTA Link</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.secondary_cta_link}
                        onChange={(e) => setFormData({ ...formData, secondary_cta_link: e.target.value })}
                        placeholder="Auto-generated from title"
                        style={{ backgroundColor: formData.secondary_cta_link && formData.secondary_cta_link.startsWith('/promo/') ? '#f0fdf4' : 'white' }}
                      />
                      {formData.secondary_cta_link && formData.secondary_cta_link.startsWith('/promo/') && (
                        <small style={{ color: '#16a34a', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                          ✅ Auto-generated unique landing page URL
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Theme *</label>
                <select
                  className="form-input"
                  value={formData.theme}
                  onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                  required
                >
                  <option value="professional">Professional (Blue gradient)</option>
                  <option value="colorful">Colorful (Purple gradient)</option>
                  <option value="festive">Festive (Pink-Red gradient)</option>
                  <option value="custom">Custom (Choose colors)</option>
                </select>
              </div>

              {formData.theme === 'custom' && (
                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Background Color</label>
                    <input
                      type="color"
                      className="form-input"
                      value={formData.background_color}
                      onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Text Color</label>
                    <input
                      type="color"
                      className="form-input"
                      value={formData.text_color}
                      onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                    />
                  </div>
                </div>
              )}

              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Start Date (Optional)</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">End Date (Optional)</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Dismiss Behavior</label>
                  <select
                    className="form-input"
                    value={formData.dismiss_behavior}
                    onChange={(e) => setFormData({ ...formData, dismiss_behavior: e.target.value })}
                  >
                    <option value="session">Session (New tab)</option>
                    <option value="24hrs">24 Hours</option>
                    <option value="7days">7 Days</option>
                    <option value="never">Never (Always show)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Rotation Interval (s)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.rotation_interval}
                    onChange={(e) => setFormData({ ...formData, rotation_interval: parseInt(e.target.value) })}
                    min={3}
                    max={30}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                    min={0}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.auto_rotate}
                    onChange={(e) => setFormData({ ...formData, auto_rotate: e.target.checked })}
                  />
                  <span>Auto-rotate (for banners with multiple announcements)</span>
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span>Active (Show immediately)</span>
                </label>
              </div>

              {/* Lead Capture Section */}
              <div style={{ padding: '1rem', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', marginTop: '1rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#166534', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  🎯 Lead Capture Landing Page
                </h3>
                
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.enable_lead_capture}
                      onChange={(e) => setFormData({ ...formData, enable_lead_capture: e.target.checked })}
                    />
                    <span>Enable Public Lead Capture Page</span>
                  </label>
                  <small style={{ color: '#6b7280', display: 'block', marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                    Creates a public landing page at /promo/[slug] for lead generation
                  </small>
                </div>

                {formData.enable_lead_capture && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Public URL Slug</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.public_slug}
                        onChange={(e) => setFormData({ ...formData, public_slug: e.target.value })}
                        placeholder="Auto-generated from title"
                        style={{ backgroundColor: formData.public_slug ? '#f0fdf4' : 'white' }}
                      />
                      {formData.public_slug ? (
                        <small style={{ color: '#16a34a', display: 'block', marginTop: '0.5rem' }}>
                          ✅ Public URL: /promo/{formData.public_slug}
                        </small>
                      ) : (
                        <small style={{ color: '#6b7280', display: 'block', marginTop: '0.5rem' }}>
                          Auto-generated from title. You can customize if needed.
                        </small>
                      )}
                    </div>

                    {/* Show full URL after saving */}
                    {editingAnnouncement && formData.public_slug && (
                      <div style={{ 
                        padding: '0.75rem', 
                        background: '#dbeafe', 
                        border: '1px solid #3b82f6',
                        borderRadius: '6px',
                        marginTop: '0.5rem'
                      }}>
                        <div style={{ fontSize: '0.75rem', color: '#1e40af', fontWeight: '600', marginBottom: '0.25rem' }}>
                          📍 Public Landing Page URL:
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.5rem',
                          padding: '0.5rem',
                          background: 'white',
                          borderRadius: '4px'
                        }}>
                          <code style={{ 
                            flex: 1,
                            fontSize: '0.875rem', 
                            color: '#1e40af',
                            wordBreak: 'break-all'
                          }}>
                            {window.location.origin}/promo/{formData.public_slug}
                          </code>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/promo/${formData.public_slug}`);
                              toast({ title: "Success", description: "URL copied to clipboard!" });
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingAnnouncement(null);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingAnnouncement ? 'Update' : 'Create'} Announcement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Automation Settings Modal */}
      {showSettings && (
        <div className="modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>Automation Settings</h2>
              <button onClick={() => setShowSettings(false)} className="modal-close">&times;</button>
            </div>
            
            <div className="modal-body">
              {/* Enable/Disable Auto-Generation */}
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={automationSettings.auto_generation_enabled}
                    onChange={(e) => setAutomationSettings({
                      ...automationSettings,
                      auto_generation_enabled: e.target.checked
                    })}
                  />
                  <span>Enable Automatic Announcement Generation</span>
                </label>
                <small className="form-help">
                  When enabled, the system will automatically generate announcements based on business metrics and events.
                </small>
              </div>

              {/* Generation Frequency */}
              <div className="form-group">
                <label className="form-label">Generation Frequency</label>
                <select
                  className="form-input"
                  value={automationSettings.generation_frequency_hours}
                  onChange={(e) => setAutomationSettings({
                    ...automationSettings,
                    generation_frequency_hours: parseInt(e.target.value)
                  })}
                  disabled={!automationSettings.auto_generation_enabled}
                >
                  <option value="1">Every Hour</option>
                  <option value="2">Every 2 Hours</option>
                  <option value="3">Every 3 Hours</option>
                  <option value="4">Every 4 Hours</option>
                  <option value="6">Every 6 Hours</option>
                  <option value="8">Every 8 Hours</option>
                  <option value="12">Every 12 Hours</option>
                  <option value="24">Once Daily</option>
                  <option value="48">Every 2 Days</option>
                  <option value="72">Every 3 Days</option>
                  <option value="168">Once Weekly</option>
                </select>
                <small className="form-help">
                  How often the system should check and generate new announcements.
                </small>
              </div>

              {/* Default CTA Link */}
              <div className="form-group">
                <label className="form-label">Default CTA Link</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="/contact"
                  value={automationSettings.default_cta_link}
                  onChange={(e) => setAutomationSettings({
                    ...automationSettings,
                    default_cta_link: e.target.value
                  })}
                />
                <small className="form-help">
                  Default link for Call-to-Action buttons in auto-generated announcements. Use your lead capturing landing page URL (e.g., /contact, /promo/summer-special).
                </small>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowSettings(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={updateAutomationSettings}
                  className="btn-primary"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementsManager;
